import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, ArrowLeft } from "lucide-react";

export default function Impressum() {
  const navigate = useNavigate();
  const close = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div
      className="min-h-screen bg-[#FFFBFB] font-body text-qh-ink"
      data-testid="impressum-page"
    >
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-qh-line">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0" data-testid="impressum-logo">
            <img
              src="/assets/qhearts-logo.png"
              alt="QHearts"
              className="h-14 sm:h-16 w-auto object-contain select-none"
              draggable="false"
            />
          </Link>
          <button
            onClick={close}
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-qh-slate hover:text-qh-ink rounded-full border border-qh-line px-4 py-2 transition-colors"
            data-testid="impressum-back"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück
          </button>
          <button
            onClick={close}
            className="sm:hidden h-10 w-10 rounded-full border border-qh-line bg-white flex items-center justify-center"
            aria-label="Schließen"
            data-testid="impressum-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-20"
      >
        <span className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
          Rechtliches
        </span>
        <h1 className="mt-3 font-display font-medium text-4xl sm:text-5xl tracking-tight leading-tight">
          Impressum
        </h1>
        <p className="mt-5 text-lg text-qh-slate leading-relaxed">
          Angaben gemäß § 5 TMG für www.qhearts.me.
        </p>

        <article className="mt-14 space-y-12 text-[15px] sm:text-base leading-relaxed">
          <Block title="Anbieter">
            <p className="font-semibold text-qh-ink">
              QHUG (haftungsbeschränkt)
            </p>
            <p className="text-qh-ink/90">
              Schwarzwaldstraße 3
              <br />
              68163 Mannheim
              <br />
              Deutschland
            </p>
          </Block>

          <Block title="Vertreten durch">
            <p className="text-qh-ink/90">
              <span className="block text-[11px] uppercase tracking-wider text-qh-slate mb-1">
                Geschäftsführer
              </span>
              Johannes Bender
            </p>
          </Block>

          <Block title="Kontakt">
            <dl className="grid sm:grid-cols-[120px_1fr] gap-y-2 gap-x-4">
              <dt className="text-qh-slate text-sm uppercase tracking-wider">
                E-Mail
              </dt>
              <dd>
                <a
                  href="mailto:info@qhearts.me"
                  className="text-qh-rose hover:text-qh-rosedark underline-offset-2 hover:underline"
                  data-testid="impressum-email"
                >
                  info@qhearts.me
                </a>
              </dd>
              <dt className="text-qh-slate text-sm uppercase tracking-wider">
                Telefon
              </dt>
              <dd>
                <a
                  href="tel:+4917664110710"
                  className="text-qh-rose hover:text-qh-rosedark underline-offset-2 hover:underline"
                  data-testid="impressum-phone"
                >
                  +49 176 64110710
                </a>
              </dd>
            </dl>
          </Block>

          <Block title="Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV">
            <p className="text-qh-ink/90">
              Johannes Bender
              <br />
              Schwarzwaldstraße 3
              <br />
              68163 Mannheim
            </p>
          </Block>

          <div className="rounded-3xl border border-qh-line bg-white p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-full bg-[#FFE4E6] text-qh-rose flex items-center justify-center font-display text-lg font-semibold shrink-0">
                18+
              </span>
              <div>
                <h3 className="font-display text-xl font-medium">Hinweis</h3>
                <p className="mt-2 text-qh-ink/90">
                  Die Inhalte dieser Website richten sich ausschließlich an
                  Personen ab 18 Jahren. Die Nutzung der Plattform ist nur
                  volljährigen Personen gestattet.
                </p>
              </div>
            </div>
          </div>
        </article>

        <div className="mt-16 pt-8 border-t border-qh-line text-sm text-qh-slate">
          <p>
            <span className="font-semibold text-qh-ink">Stand:</span>{" "}
            {new Date().toLocaleDateString("de-DE", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
          <p className="mt-1">© {new Date().getFullYear()} QHUG (haftungsbeschränkt)</p>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <button
            onClick={close}
            className="rounded-full bg-qh-ink hover:bg-qh-ink/90 text-white h-12 px-8 text-sm font-medium inline-flex items-center justify-center gap-2"
            data-testid="impressum-close-bottom"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zur Seite
          </button>
          <Link
            to="/agb"
            className="rounded-full border border-qh-line bg-white hover:bg-[#FFFBFB] h-12 px-8 text-sm font-medium inline-flex items-center justify-center transition-colors"
          >
            Zu den AGB
          </Link>
          <Link
            to="/datenschutz"
            className="rounded-full border border-qh-line bg-white hover:bg-[#FFFBFB] h-12 px-8 text-sm font-medium inline-flex items-center justify-center transition-colors"
          >
            Datenschutz
          </Link>
        </div>
      </motion.main>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-medium text-qh-ink">
        {title}
      </h2>
      <div className="mt-4 space-y-2">{children}</div>
    </section>
  );
}
